'use client';

// UnifiedContentPanel - Unified view of all content sources with drag-and-drop

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { useTranslations } from 'next-intl';
import { Button } from '@boty/ui';
import { useContentSources } from './useContentSources';
import { ContentSourceList } from './ContentSourceList';
import { ContentSummary } from './ContentSummary';
import { ContentLimitWarning } from './ContentLimitWarning';
import type { ContentSource } from '../../types/content-source';

export interface UnifiedContentPanelProps {
  /** Bot ID to fetch content for */
  botId: string | null;
  /** Callback when proceed button is clicked */
  onProceed: () => void;
  /** Callback when edit is clicked on a text content source */
  onEditText?: ((contentId: string, content: string) => void) | undefined;
  /** Callback when a content source is deleted (for syncing with local hooks) */
  onDelete?: (contentId: string) => void;
  /** Whether proceed is disabled externally */
  disabled?: boolean;
  /** Whether this is a session recovery (returning user) */
  isSessionRecovery?: boolean;
  /** Custom label for the proceed button */
  proceedButtonLabel?: string | undefined;
  /** Custom icon for the proceed button */
  proceedButtonIcon?: React.ReactNode | undefined;
  /** Hide the inline content list (when shown in a separate left sidebar on desktop) */
  hideInlineList?: boolean;
  /** Optimistic text entry to show immediately while saving */
  optimisticText?: { content: string; characterCount: number } | null;
}

/**
 * UnifiedContentPanel - Display all content sources in a unified view
 *
 * Features:
 * - Fetches content from database via useContentSources
 * - Displays ContentSourceList with drag-and-drop reordering
 * - Shows ContentSummary with total counts and proceed button
 * - Shows ContentLimitWarning when over 100K characters
 * - Session recovery message for returning users
 *
 * @example
 * ```tsx
 * <UnifiedContentPanel
 *   botId={sharedBotId}
 *   onProceed={() => router.push('/test')}
 * />
 * ```
 */
export function UnifiedContentPanel({
  botId,
  onProceed,
  onEditText,
  onDelete,
  disabled = false,
  isSessionRecovery = false,
  proceedButtonLabel,
  proceedButtonIcon,
  hideInlineList = false,
  optimisticText,
}: UnifiedContentPanelProps) {
  const t = useTranslations('creator.contentSources');
  const tSession = useTranslations('creator.contentSources.session');

  // Track if we've shown the welcome back message
  const [showWelcomeBack, setShowWelcomeBack] = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);

  const {
    sources,
    summary,
    isLoading,
    totalCharacters,
    isProcessing,
    allReady,
    deleteSource,
    reorderSources,
    isDeleting,
    isReordering,
  } = useContentSources({
    botId,
    enabled: !!botId,
  });

  // Show welcome back message when session is recovered and content exists
  useEffect(() => {
    if (isSessionRecovery && sources.length > 0 && !welcomeDismissed) {
      setShowWelcomeBack(true);
    }
  }, [isSessionRecovery, sources.length, welcomeDismissed]);

  // Get the most recent update date from sources
  const lastModifiedDate = sources.length > 0
    ? new Date(
        Math.max(...sources.map((s) => new Date(s.updatedAt).getTime()))
      )
    : null;

  // Format date for display
  const formatDate = (date: Date) => {
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Merge optimistic text entry into display sources (for mobile inline list)
  const displaySources = useMemo(() => {
    if (!optimisticText || hideInlineList) return sources;

    const optimisticSource: ContentSource = {
      id: 'optimistic-text',
      botId: botId ?? 'pending',
      type: 'text',
      title: optimisticText.content.substring(0, 50) + (optimisticText.content.length > 50 ? '...' : ''),
      content: optimisticText.content,
      characterCount: optimisticText.characterCount,
      status: 'processing',
      displayOrder: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    return [optimisticSource, ...sources];
  }, [sources, optimisticText, botId, hideInlineList]);

  // Wrapper for delete that also notifies parent for local state sync
  const handleDelete = useCallback(
    async (contentId: string) => {
      await deleteSource(contentId);
      // Notify parent to sync local hook state
      onDelete?.(contentId);
    },
    [deleteSource, onDelete]
  );

  // Ref for the content list container to scroll into view on mobile
  const contentListRef = useRef<HTMLDivElement>(null);
  // Track only saved sources (excluding optimistic text) so typing doesn't
  // trigger scroll-away from the textarea on mobile
  const prevSourceCountRef = useRef(sources.length);

  // Auto-scroll to content list on mobile when new content is added
  useEffect(() => {
    if (hideInlineList) return; // Desktop uses sidebar, no need to scroll
    const prevCount = prevSourceCountRef.current;
    prevSourceCountRef.current = sources.length;

    // Don't scroll while user is typing in a textarea/input
    const active = document.activeElement;
    const isTyping = active?.tagName === 'TEXTAREA' || active?.tagName === 'INPUT';

    // Scroll into view when SAVED sources increase (not optimistic typing)
    if (sources.length > prevCount && sources.length > 0 && !isTyping && contentListRef.current) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      contentListRef.current.scrollIntoView({
        behavior: prefersReducedMotion ? 'auto' : 'smooth',
        block: 'nearest',
      });
    }
  }, [sources.length, hideInlineList]);

  // Don't render if no bot or no sources (and not loading), unless optimistic entry exists
  if (!optimisticText && (!botId || (sources.length === 0 && !isLoading))) {
    return null;
  }

  const isInteractionDisabled = disabled || isDeleting || isReordering;

  return (
    <Box sx={{ mt: 3 }}>
      {/* Inline content list - hidden when displayed in left sidebar on desktop */}
      {!hideInlineList && (
        <Box ref={contentListRef}>
          {/* Welcome back message for returning users */}
          {showWelcomeBack && (
            <Alert
              severity="info"
              onClose={() => {
                setShowWelcomeBack(false);
                setWelcomeDismissed(true);
              }}
              sx={{ mb: 2 }}
            >
              <Typography variant="body2" fontWeight="medium">
                {tSession('welcomeBack')}
              </Typography>
              {lastModifiedDate && (
                <Typography variant="caption" color="text.secondary">
                  {tSession('lastModified', { date: formatDate(lastModifiedDate) })}
                </Typography>
              )}
            </Alert>
          )}

          {/* Show loading state for content list */}
          {isLoading && !optimisticText ? (
            <Box sx={{ py: 2 }}>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                {t('loading')}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Content limit warning */}
              <ContentLimitWarning
                totalCharacters={totalCharacters}
                sources={sources}
                onDelete={handleDelete}
              />

              {/* Content source list with drag-and-drop */}
              <ContentSourceList
                sources={displaySources}
                onReorder={reorderSources}
                onDelete={handleDelete}
                onEdit={onEditText}
                disabled={isInteractionDisabled}
                showEmptyState={false}
              />
            </>
          )}
        </Box>
      )}

      {/* Spacer for sticky summary - show when bot exists and list is visible */}
      {botId && !hideInlineList && <Box sx={{ height: 100 }} />}

      {/* Content summary with proceed button - sticky at bottom */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          // Use logical properties so the bar spans full width in both LTR and RTL.
          // Plain `left: 0; right: 0` gets flipped by stylis-plugin-rtl and can shift the
          // bar off-screen in some Hebrew layouts.
          insetInlineStart: 0,
          insetInlineEnd: 0,
          zIndex: 1100,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ maxWidth: 700, mx: 'auto' }}>
          {/* Show loading button while fetching content, otherwise show full summary */}
          {/* On desktop (hideInlineList), skip loading spinner — sidebar handles feedback */}
          {isLoading && !hideInlineList ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', py: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={onProceed}
                disabled={disabled}
                loading
              >
                {t('loading')}
              </Button>
            </Box>
          ) : (
            <ContentSummary
              summary={summary}
              isProcessing={isProcessing}
              allReady={allReady}
              onProceed={onProceed}
              disabled={isInteractionDisabled}
              proceedButtonLabel={proceedButtonLabel}
              proceedButtonIcon={proceedButtonIcon}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default UnifiedContentPanel;
